/* Oricle cart (the drawer and the /cart page): AJAX cart on Shopify's Cart API + Section Rendering
   API. The drawer is registered as <cart-drawer> so Dawn's product-form.js (accessory PDPs) can drive it with the
   same renderContents / getSectionsToRender contract, and Oricle's own product form does too.

   This is the checkout path for a roughly $200 device bought by people aged 60–80, so:
   · one request at a time — a second tap while one is in flight is ignored, never queued on
     stale numbers; quantities come from the rendered line's data-qty, not from visible text;
   · the Pro upgrade ADDS the new line before it removes the old one, so a failure can leave an
     extra line the customer can see and remove, never an empty cart;
   · the swap is one press (design 10/1): a confirmation row ("Upgraded to {model} · {amount} added
     to your subtotal · Undo") shows under the new line for 3.2 s, then fades and the line itself
     carries "Upgraded from {model} · Undo". The record of what was swapped lives on this instance,
     both rows are drawn from it after every re-render, and it is dropped when the drawer closes or
     the upgraded line changes;
   · an add-on is a tick-box tile (D3, Jackson 10/6): a tap adds it, a second tap removes its line;
     focus stays on the tile through the re-render and the status region names the change. In an
     accessory-only cart an added add-on is an ordinary line, which takes focus and settles from a
     faint fill;
   · after an add from a page, the header says "Added" in place of the count for a moment;
   · a line's key is not stable: when a discount code is split across lines, adding one line
     re-keys the others. Each step of the swap and its Undo finds its line again in the freshly
     rendered drawer (by key, then by variant) instead of trusting a key read before the add;
   · every change re-renders the drawer body, which destroys the button that was pressed, so
     focus is put back on the equivalent control (or the close button if the line is gone);
   · a persistent status node outside the re-rendered body announces the new count and
     subtotal — a live region that is itself replaced is not reliably announced;
   · errors are plain sentences and stay until the next successful action;
   · the /cart page is the same section in the page flow (<oricle-cart-page>), driven by this same
     code: one controller, two shells. The drawer is not rendered on /cart, so a page never holds
     two carts, two live regions or two sets of ids;
   · a refetch (the fallback when nothing else carries fresh markup) asks for HTML sections without
     an Accept: application/json header: with it, Shopify answers a product or /cart URL with that
     page's JSON and the sections never arrive. */
(() => {
  const DrawerBase = window.OricleDrawer || HTMLElement;
  const DRAWER_ID = 'oricle-cart-drawer';
  const ACTIONS = '[data-o-line-step],[data-o-line-remove],[data-o-addon-add],[data-o-addon-remove],[data-o-swap],[data-o-undo]';
  const esc = (v) => (window.CSS && CSS.escape ? CSS.escape(v) : v);
  // Dawn declares PUB_SUB_EVENTS with a top-level const (constants.js), so it is a global binding
  // but never a property of window.
  const pubsub = () => (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function' && typeof publish === 'function' ? PUB_SUB_EVENTS : null);

  /* Everything a cart does, for either shell. The shell supplies isOpen/open/close. */
  const CartMixin = (Base) => class extends Base {
    connectedCallback() {
      if (super.connectedCallback) super.connectedCallback();
      if (this._cartBound) return;
      this._cartBound = true;
      this.addEventListener('click', (e) => this.onClick(e));
      // scroll does not bubble, but it can be captured: one listener survives every re-render.
      this.onMeasure = () => this.measure();
      this.addEventListener('scroll', this.onMeasure, true);
      window.addEventListener('resize', this.onMeasure);
      this.measure();
    }
    disconnectedCallback() {
      if (super.disconnectedCallback) super.disconnectedCallback();
      window.removeEventListener('resize', this.onMeasure);
    }

    get sectionId() { return this.dataset.sectionId || DRAWER_ID; }
    get innerId() { return this.dataset.innerId || 'OricleCartDrawerInner'; }
    getSectionsToRender() { return [{ id: this.sectionId, selector: '#' + this.innerId }, { id: 'cart-icon-bubble' }]; }

    msg(key, fallback) { return this.dataset[key] || fallback; }

    /* Every render bumps `_seq`; a refetch applies only if nothing rendered after it started. */
    refresh() {
      const seq = this._seq = (this._seq || 0) + 1;
      const ids = [this.sectionId, 'cart-icon-bubble'].join(',');
      const url = `${window.location.pathname}?sections=${ids}&_=${Date.now()}`;
      return fetch(url, { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null))
        .then((sections) => { if (sections && sections[this.sectionId] && seq === this._seq) this.applySections(sections, undefined, { silent: true }); }).catch(() => {});
    }

    /* `silent`: publish nothing (a refetch is already the latest word). */
    applySections(sections, restore, opts = {}) {
      const html = sections[this.sectionId];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.getElementById(this.innerId);
        const cur = this.querySelector('#' + esc(this.innerId));
        const root = doc.querySelector('[data-o-cart-root]');
        if (root) { this.dataset.count = root.dataset.count || '0'; this.dataset.subtotal = root.dataset.subtotal || ''; this.dataset.totalCents = root.dataset.totalCents || '0'; }
        if (next && cur) { cur.replaceWith(next); this.measure(); this.validateUndo(); this.renderUndo(); this.restoreFocus(restore); this.announce(); }
      }
      const bubble = sections['cart-icon-bubble'];
      const pill = document.getElementById('cart-icon-bubble');
      if (bubble && pill) {
        const inner = new DOMParser().parseFromString(bubble, 'text/html').querySelector('.shopify-section');
        if (inner) pill.innerHTML = inner.innerHTML;
      }
      if (opts.silent) return;
      const events = pubsub();
      if (events) publish(events.cartUpdate, { source: 'oricle-cart' });
    }

    /* Put focus back where the customer was. `restore` names the control by line key + action. */
    restoreFocus(restore) {
      if (!this.isOpen) return;
      // With no explicit target, act only when the re-render destroyed the focused control: in the
      // drawer, when focus fell out of it (a modal must never leave focus on the page behind it); on
      // the page, when it fell to the body.
      if (!restore && (this.contains(document.activeElement) || (this.isPage && document.activeElement && document.activeElement !== document.body))) return;
      restore = restore || {};
      const sel = restore.key ? `[data-line-key="${esc(restore.key)}"]` : '';
      const candidates = [
        restore.action && sel ? `${restore.action}${sel}` : null,
        sel ? `[data-o-line]${sel} [data-o-line-step="1"]` : null,
        restore.fallback || null,
        '[data-o-line] [data-o-line-step="1"]',
        '[data-o-focus-fallback]',
      ].filter(Boolean);
      for (const c of candidates) { const el = this.querySelector(c); if (el && !el.disabled) { el.focus(); return; } }
    }

    announce(extra) {
      const status = this.querySelector('[data-o-cart-status]');
      if (!status) return;
      const count = parseInt(this.dataset.count || '0', 10);
      const tpl = extra && extra.only ? '' : count === 0 ? this.msg('statusEmpty', 'Your cart is empty.') : count === 1 ? this.msg('statusOne', 'Cart updated: 1 item, subtotal {subtotal}.') : this.msg('statusMany', 'Cart updated: {count} items, subtotal {subtotal}.');
      const tail = extra && extra.only ? extra.only : extra;
      status.textContent = (tpl.replace('{count}', count).replace('{subtotal}', this.dataset.subtotal || '') + (tail ? ' ' + tail : '')).trim();
    }

    /* —— Undo for the upgrade swap. `this.undo` is the only record: which line was added (key and
       its quantity then), which variant was removed and how many, and the original's name. —— */
    lineByKey(key) { return this.querySelector(`[data-o-line][data-line-key="${esc(key)}"]`); }
    /* The line as it is rendered now: by its key if that survived, else the one line of that
       variant that is not `except` (the other half of a swap). */
    findLine(key, variantId, except) {
      const byKey = key ? this.lineByKey(key) : null;
      if (byKey) return byKey;
      if (!variantId) return null;
      return [...this.querySelectorAll(`[data-o-line][data-variant-id="${variantId}"]`)].find((l) => l.dataset.lineKey !== except) || null;
    }
    validateUndo() {
      const u = this.undo;
      if (!u) return;
      const line = this.findLine(u.addedKey, u.addedVariantId);
      if (line) u.addedKey = line.dataset.lineKey;
      if (!line || (parseInt(line.dataset.qty, 10) || 0) !== u.addedQty) this.undo = null;
    }
    renderUndo() {
      const hadFocus = [...this.querySelectorAll('[data-o-undo-row]')].some((el) => el.contains(document.activeElement));
      this.querySelectorAll('[data-o-undo-row]').forEach((el) => el.remove());
      const u = this.undo;
      if (!u) return;
      const line = this.findLine(u.addedKey, u.addedVariantId);
      const body = line && line.querySelector('.o-line__body');
      if (!body) return;
      // For a moment after the swap: the confirmation row under the line, then (renderUndo again) the line's own note.
      if (u.flashUntil && Date.now() < u.flashUntil) {
        const done = document.createElement('div');
        done.className = 'o-line__done'; done.setAttribute('data-o-undo-row', '');
        const tick = document.createElement('span'); tick.className = 'o-line__done-tick'; tick.setAttribute('aria-hidden', 'true');
        tick.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
        const words = document.createElement('span'); words.className = 'o-line__done-text';
        const head = document.createElement('span'); head.className = 'o-line__done-head';
        head.textContent = this.msg('upsellDone', 'Upgraded to {model}').replace('{model}', u.upName || '');
        const sub = document.createElement('span'); sub.className = 'o-line__done-sub';
        sub.textContent = this.msg('upsellDoneSub', '{amount} added to your subtotal').replace('{amount}', (u.delta || '').replace(/^\+/, ''));
        words.append(head, sub);
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'o-ctl o-line__undo-btn'; b.setAttribute('data-o-undo', '');
        b.textContent = this.msg('undoLabel', 'Undo');
        b.setAttribute('aria-label', `${this.msg('undoLabel', 'Undo')}: ${head.textContent}`);
        done.append(tick, words, b);
        line.after(done);
        clearTimeout(this._flashT);
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this._flashT = setTimeout(() => {
          done.classList.add('is-leaving');
          this._flashT = setTimeout(() => {
            if (this.undo !== u) return;
            u.flashUntil = 0;
            this.renderUndo();
            if (done.contains(document.activeElement) || !this.contains(document.activeElement)) { const nb = this.querySelector('[data-o-undo]'); if (nb && this.isOpen) nb.focus(); }
          }, reduce ? 0 : 280);
        }, Math.max(0, u.flashUntil - Date.now()));
        if (hadFocus) b.focus();
        return;
      }
      const row = document.createElement('div');
      row.className = 'o-line__undo'; row.setAttribute('data-o-undo-row', '');
      const text = document.createElement('span');
      text.className = 'o-line__undo-text';
      text.textContent = this.msg('upgradedFrom', 'Upgraded from {name}').replace('{name}', u.name);
      const btn = document.createElement('button');
      btn.type = 'button'; btn.className = 'o-ctl o-line__undo-btn'; btn.setAttribute('data-o-undo', '');
      btn.textContent = this.msg('undoLabel', 'Undo');
      btn.setAttribute('aria-label', `${this.msg('undoLabel', 'Undo')}: ${text.textContent}`);
      row.append(text, btn);
      const controls = body.querySelector('.o-line__controls');
      if (controls) body.insertBefore(row, controls); else body.appendChild(row);
      if (hadFocus) btn.focus();
    }

    /* After an add-on is added: its tile when it has one (focus is already back on it), otherwise its new
       line, which takes focus and settles from a faint fill; either way it is named in the status. */
    focusAdded(variantId, name) {
      const tile = this.querySelector(`[data-o-addon][data-variant-id="${variantId}"]`);
      const line = tile ? null : [...this.querySelectorAll(`[data-o-line][data-variant-id="${variantId}"]`)].pop();
      if (tile && this.isOpen && document.activeElement !== tile) tile.focus();
      if (line) {
        line.setAttribute('tabindex', '-1');
        line.classList.add('is-new');
        line.focus({ preventScroll: false });
        setTimeout(() => line.classList.remove('is-new'), 1600);
      }
      if (name) this.announce({ only: this.msg('addedNote', '{name} added to your cart').replace('{name}', name) });
    }

    async request(url, body, restore) {
      this._seq = (this._seq || 0) + 1; // a refetch already in flight is now older than this change
      this._busy = true;
      this.setAttribute('aria-busy', 'true');
      try {
        let res;
        try {
          res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(Object.assign({ sections: this.getSectionsToRender().map((s) => s.id), sections_url: window.location.pathname }, body)) });
        } catch (networkError) {
          throw new Error(this.msg('errorNetwork', 'We could not reach the cart. Please check your connection and try again.'));
        }
        let data = null;
        try { data = await res.json(); } catch (parseError) { data = null; }
        if (!res.ok || !data || data.status) throw new Error((data && (data.description || data.message)) || this.msg('errorGeneric', 'Something went wrong updating your cart. Please try again.'));
        this.clearError();
        this._seq += 1; // and so is one started while this change was on its way
        this.applySections(data.sections || {}, restore);
        return data;
      } finally {
        this._busy = false;
        this.removeAttribute('aria-busy');
      }
    }

    showError(message) {
      let el = this.querySelector('[data-o-cart-error]');
      if (!el) return;
      el.textContent = message; el.hidden = false;
    }
    clearError() { const el = this.querySelector('[data-o-cart-error]'); if (el) { el.hidden = true; el.textContent = ''; } }

    /* Adds go to /cart/add.js, not /cart/add: asked for JSON, /cart/add answers with a short line (no product url,
       type or image), and Shopify builds every pixel's product_added_to_cart from that answer. Microsoft Clarity's
       pixel reads product.url and throws on the short line, so it never records the add. */
    add(items, openAfter = true, restore) {
      return this.request(`${window.routes.cart_add_url}.js`, { items }, restore).then((d) => { if (openAfter) { this.open(); this.flashAdded(); } return d; });
    }

    /* "Added" in the header, in place of the count, for a moment after an add from a page. Drawn after the
       re-render; any later render replaces the header and so ends it early. */
    flashAdded() {
      const label = this.dataset.addedChip;
      const head = this.querySelector('.o-cart__head');
      if (!label || !head || this.isPage) return;
      const title = head.querySelector('.o-cart__title');
      if (!title || title.querySelector('.o-cart__added')) return;
      const chip = document.createElement('span');
      chip.className = 'o-cart__added';
      chip.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
      chip.append(document.createTextNode(label));
      title.classList.add('is-added');
      title.append(chip);
      clearTimeout(this._addedT);
      this._addedT = setTimeout(() => { chip.classList.add('is-leaving'); setTimeout(() => { chip.remove(); title.classList.remove('is-added'); }, 400); }, 2500);
    }
    change(key, quantity, restore) { return this.request(window.routes.cart_change_url, { id: key, quantity }, restore); }

    /* Add the upgrade first; only then remove the original line. Both facts the Undo needs —
       the original's variant and quantity — are read from the rendered line BEFORE anything
       changes, and the added line's key comes from the add response. */
    async swap(key, variantId, offer) {
      offer = offer || {};
      const line = this.lineByKey(key);
      const qty = line ? parseInt(line.dataset.qty, 10) || 1 : 1;
      const original = { variantId: line ? parseInt(line.dataset.variantId, 10) : NaN, qty, name: line && line.querySelector('.o-line__name') ? line.querySelector('.o-line__name').textContent.trim() : '' };
      this.undo = null;
      const added = await this.request(`${window.routes.cart_add_url}.js`, { items: [{ id: variantId, quantity: qty }] }, { fallback: '[data-o-line] [data-o-line-step="1"]' });
      const addedKey = added && added.items && added.items[0] ? added.items[0].key : (added && added.key) || null;
      // The add can re-key the original line (a code's split is recalculated), so find it again.
      const now = this.findLine(key, original.variantId, addedKey);
      try {
        if (!now) throw new Error('original line not found');
        await this.change(now.dataset.lineKey, 0, { fallback: '[data-o-line] [data-o-line-step="1"]' });
      } catch (err) {
        throw new Error(this.msg('errorSwap', 'We added the upgrade but could not remove the original pair. Please remove it below.'));
      }
      const addedLine = this.findLine(addedKey, variantId);
      if (addedLine && original.variantId) {
        this.undo = { addedKey: addedLine.dataset.lineKey, addedVariantId: variantId, addedQty: parseInt(addedLine.dataset.qty, 10) || qty, variantId: original.variantId, qty: original.qty, name: original.name,
          upName: offer.name || '', delta: offer.delta || '', flashUntil: Date.now() + 3200 };
        this.renderUndo();
        const undoBtn = this.querySelector('[data-o-undo]');
        if (undoBtn) undoBtn.focus();
        this.announce(this.msg('statusUpgraded', 'Upgraded to {model}. An Undo button is beside it.').replace('{model}', offer.name || '').replace('{name}', original.name));
      }
    }

    /* Reverse the swap the same way it was made: bring the original back first, only then take
       the upgrade out. A failure at either step leaves the customer with more, never less. */
    async undoSwap() {
      const u = this.undo;
      if (!u) return;
      this.undo = null; clearTimeout(this._flashT); this.renderUndo();
      const back = `[data-o-line][data-variant-id="${u.variantId}"] [data-o-line-step="1"]`;
      try {
        await this.request(`${window.routes.cart_add_url}.js`, { items: [{ id: u.variantId, quantity: u.qty }] }, { fallback: back });
      } catch (err) {
        throw new Error(this.msg('errorUndo', 'We could not bring the {name} back. The upgrade stays in your cart.').replace('{name}', u.name));
      }
      const pro = this.findLine(u.addedKey, u.addedVariantId);
      if (!pro) return;
      const next = Math.max(0, (parseInt(pro.dataset.qty, 10) || 0) - u.addedQty);
      try {
        await this.change(pro.dataset.lineKey, next, { fallback: back });
      } catch (err) {
        throw new Error(this.msg('errorUndoHalf', 'We brought the {name} back but could not remove the upgrade. Please remove it below.').replace('{name}', u.name));
      }
    }

    onClick(e) {
      const t = e.target.closest(ACTIONS);
      if (!t || !this.contains(t)) return;
      if (this._busy) return; // one request at a time: a second tap never acts on stale numbers
      const key = t.dataset.lineKey;
      let job;
      if (t.hasAttribute('data-o-line-step')) {
        const line = t.closest('[data-o-line]');
        const cur = parseInt(line.dataset.qty, 10) || 1;
        const step = parseInt(t.dataset.oLineStep, 10) || 0;
        const next = Math.max(1, Math.min(parseInt(line.dataset.max, 10) || 9, cur + step));
        if (next === cur) return;
        job = this.change(key, next, { key, action: `[data-o-line-step="${step}"]` });
      } else if (t.hasAttribute('data-o-line-remove')) {
        job = this.change(key, 0, { fallback: '[data-o-line] [data-o-line-step="1"]' });
      } else if (t.hasAttribute('data-o-addon-add')) {
        const vid = parseInt(t.dataset.variantId, 10);
        job = this.add([{ id: vid, quantity: 1 }], false, { fallback: `[data-o-addon][data-variant-id="${vid}"]` }).then(() => this.focusAdded(vid, t.dataset.name));
      } else if (t.hasAttribute('data-o-addon-remove')) {
        const vid = t.dataset.variantId; const name = t.dataset.name;
        job = this.change(key, 0, { fallback: `[data-o-addon][data-variant-id="${vid}"]` }).then(() => { if (name) this.announce({ only: this.msg('removedNote', '{name} removed from your cart').replace('{name}', name) }); });
      } else if (t.hasAttribute('data-o-swap')) {
        job = this.swap(key, parseInt(t.dataset.variantId, 10), { name: t.dataset.upName, delta: t.dataset.delta });
      } else if (t.hasAttribute('data-o-undo')) {
        job = this.undoSwap();
      }
      if (job) job.catch((err) => this.showError(err && err.message ? err.message : this.msg('errorGeneric', 'Something went wrong updating your cart. Please try again.')));
    }

    /* Bottom fade: a row cut by the scroll boundary reads as clipped, not scrollable. */
    measure() {
      const scroll = this.querySelector('[data-o-cart-scroll]');
      const fade = this.querySelector('[data-o-cart-fade]');
      if (!scroll || !fade) return;
      fade.classList.toggle('is-on', scroll.scrollHeight - scroll.clientHeight - scroll.scrollTop > 8);
    }
  };

  /* The drawer: a modal over the page, opened by the cart pill and by every add. */
  class OricleCartDrawer extends CartMixin(DrawerBase) {
    connectedCallback() {
      super.connectedCallback();
      const pill = document.getElementById('cart-icon-bubble');
      if (pill && !pill.dataset.oBound) {
        pill.dataset.oBound = '1';
        pill.setAttribute('role', 'button'); pill.setAttribute('aria-haspopup', 'dialog');
        pill.addEventListener('click', (e) => { e.preventDefault(); this.open(pill); });
      }
    }
    open(trigger) { super.open(trigger); requestAnimationFrame(() => this.measure()); }
    close() { this.undo = null; clearTimeout(this._flashT); clearTimeout(this._addedT); this.renderUndo(); super.close(); }
    returnFocusEl() { return document.getElementById('cart-icon-bubble'); }
    /* —— Dawn contract (product-form.js) —— */
    setActiveElement(el) { this.trigger = el; }
    renderContents(parsedState, openAfter = true) {
      if (parsedState && parsedState.sections) this.applySections(parsedState.sections);
      if (openAfter) { this.open(); this.flashAdded(); }
    }
  }

  /* The /cart page: the same cart in the page flow. Always "open"; nothing to open or close. */
  class OricleCartPage extends CartMixin(HTMLElement) {
    get isOpen() { return true; }
    get isPage() { return true; }
    open() {}
    close() {}
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', OricleCartDrawer);
  if (!customElements.get('oricle-cart-page')) customElements.define('oricle-cart-page', OricleCartPage);
  window.OricleCartDrawer = OricleCartDrawer;
})();
